import React, { useState, useEffect } from "react";
import { C, font, text } from "../theme";
import { retryAfterText } from "./LoginPage";

export default function TwoFactorVerify({ onVerified, onBack }) {
  const [useBackup, setUseBackup] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [challengeDead, setChallengeDead] = useState(false);

  useEffect(() => { document.title = "Two-factor authentication — SGNIC P2P"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const body = useBackup ? { backupCode: code } : { token: code };
    let res;
    try {
      res = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch {
      setErr("Can't reach the server. Try again.");
      return;
    }
    if (!res.ok) {
      // A rate-limited (429) or failed (5xx) request is NOT a wrong code —
      // saying "Invalid code" sends users off to re-sync authenticators and
      // burn backup codes (the limiter is shared per-IP across auth routes).
      if (res.status === 429) { setErr(retryAfterText(res) + " Your code was not checked."); return; }
      if (res.status >= 500) { setErr("Something went wrong on our end. Please try again shortly."); return; }
      const b = await res.json().catch(() => ({}));
      const msg = b.error || "Invalid code";
      // The 5-minute challenge expired (UX-73): the fix is a fresh sign-in,
      // so offer it instead of leaving a dead Verify button.
      if (/challenge/i.test(msg) && /expired|progress/i.test(msg)) {
        setChallengeDead(true);
        setErr("This sign-in attempt has expired (the 2FA step must be completed within 5 minutes).");
        return;
      }
      setErr(msg);
      return;
    }
    onVerified();
  };

  return (
    <div style={{ fontFamily: font, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.gray50 }}>
      <form onSubmit={submit} style={{ background: C.white, padding: 32, borderRadius: 10, border: `1px solid ${C.gray200}`, width: 360, display: "flex", flexDirection: "column", gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: text.h1, color: C.gray1100 }}>Two-factor authentication</h1>
        <div style={{ color: C.gray500, fontSize: text.sm }}>
          {useBackup ? "Enter one of your backup codes." : "Enter the 6-digit code from your authenticator app."}
        </div>
        {err && <div role="alert" style={{ color: C.danger, fontSize: text.md }}>{err}</div>}
        {challengeDead ? (
          <button type="button" onClick={onBack} style={{ padding: "10px 12px", background: C.gray1100, color: C.white, border: "none", borderRadius: 6, cursor: "pointer" }}>
            Sign in again
          </button>
        ) : (
          <>
            <input
              autoFocus
              aria-label={useBackup ? "Backup code" : "6-digit code"}
              inputMode={useBackup ? "text" : "numeric"}
              placeholder={useBackup ? "xxxx-xxxx" : "123456"}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              style={{ padding: "10px 12px", border: `1px solid ${C.gray300}`, borderRadius: 6, letterSpacing: useBackup ? 1 : 4, textAlign: "center", fontSize: text.h2 }}
            />
            <button type="submit" style={{ padding: "10px 12px", background: C.gray1100, color: C.white, border: "none", borderRadius: 6, cursor: "pointer" }}>Verify</button>
            <button type="button" onClick={() => { setUseBackup(!useBackup); setCode(""); setErr(""); }} style={{ background: "none", border: "none", color: C.gray600, fontSize: text.sm, cursor: "pointer", textDecoration: "underline" }}>
              {useBackup ? "Use authenticator code instead" : "Use a backup code instead"}
            </button>
          </>
        )}
        {/* The page is no longer a dead end (UX-73): a way back, and a recovery
            path for the lost-phone-and-no-codes case. */}
        {onBack && !challengeDead && (
          <button type="button" onClick={onBack} style={{ background: "none", border: "none", color: C.gray600, fontSize: text.sm, cursor: "pointer", textDecoration: "underline" }}>
            Back to sign in
          </button>
        )}
        <div style={{ color: C.gray500, fontSize: text.xs, lineHeight: 1.4 }}>
          Lost your authenticator and backup codes? Ask an administrator to reset your two-factor setup — you'll re-enroll at your next sign-in.
        </div>
      </form>
    </div>
  );
}
